import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Clock, History, Home as HomeIcon, Layers, MessageCircle, User, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { MapView } from "@/components/MapView";
import { ActiveRideCard } from "@/components/ActiveRideCard";
import { RideHistory } from "@/components/RideHistory";
import { RideSummary } from "@/components/RideStatus";
import { BottomNav, SectionTitle } from "@/components/rider/Nav";
import { DriverGreeting, DriverStats, NextRide, OnlineToggle, RideRow } from "@/components/driver/DriverHome";
import { NowRides, ScheduledRides, useAvailableSets, useMySets } from "@/components/driver/RideSets";
import { DriverCalendar } from "@/components/driver/DriverCalendar";
import { DriverMessages, DriverProfile, useDriverRides } from "@/components/driver/DriverScreens";
import { WeeklyEarnings } from "@/components/driver/Earnings";
import { GpsContext, useDriverState, useOnlineGps } from "@/lib/driverGps";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ITEMS = [
  { value: "home", label: "Home", icon: HomeIcon }, { value: "now", label: "Now", icon: Zap }, { value: "scheduled", label: "Scheduled", icon: Layers },
  { value: "calendar", label: "Calendar", icon: CalendarDays }, { value: "history", label: "History", icon: History },
  { value: "messages", label: "Messages", icon: MessageCircle }, { value: "profile", label: "Profile", icon: User },
];
const DRIVING = ["assigned", "en_route", "arrived", "in_progress"];

function RideDialog({ ride, onClose }) {
  return (
    <Dialog open={!!ride} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card sm:max-w-md" data-testid="driver-ride-dialog">
        <DialogHeader><DialogTitle className="font-display">Ride details</DialogTitle></DialogHeader>
        {ride && (DRIVING.includes(ride.status) && ride.driver_id ? <ActiveRideCard ride={ride} /> : <RideSummary ride={ride} perspective="driver" />)}
      </DialogContent>
    </Dialog>
  );
}

function Home({ active, go, onOpen }) {
  const { data: rides } = useDriverRides();
  const { data: avail } = useAvailableSets();
  const { data: mine } = useMySets();
  const { data: history } = useQuery({ queryKey: ["driverHistory"], queryFn: () => api.get("/driver/history").then((r) => r.data) });
  const today = (rides || []).filter((r) => new Date(r.scheduled_at).toDateString() === new Date().toDateString());
  const counts = { today: today.length, available: avail?.length ?? 0, accepted: mine?.length ?? 0, completed: (history || []).filter((r) => r.status === "completed").length };
  return (
    <div className="space-y-5" data-testid="driver-dashboard">
      <DriverGreeting onProfile={() => go("profile")} />
      <OnlineToggle />
      {active && (
        <section className="space-y-2" data-testid="driver-current-ride">
          <SectionTitle>Current ride</SectionTitle>
          <ActiveRideCard ride={active} />
        </section>
      )}
      <DriverStats go={go} counts={counts} />
      <WeeklyEarnings />
      <NextRide onOpen={onOpen} />
      <section className="space-y-2" data-testid="driver-today">
        <SectionTitle><span className="flex items-center gap-1.5"><Clock className="h-3 w-3" />Today's assigned rides</span></SectionTitle>
        {!today.length && <p className="text-sm text-muted-foreground">No rides today.</p>}
        {today.map((r) => <RideRow key={r.id} ride={r} onOpen={onOpen} />)}
      </section>
    </div>
  );
}

export default function DriverMode() {
  const [tab, setTab] = useState("home");
  const [setsTab, setSetsTab] = useState("available");
  const [open, setOpen] = useState(null);
  const { data: state } = useDriverState();
  const gps = useOnlineGps(!!state?.online);
  const { data: active } = useQuery({ queryKey: ["driverActive"], queryFn: () => api.get("/driver/active").then((r) => r.data), refetchInterval: 5000 });
  const go = (t, sub) => { setTab(t); if (sub) setSetsTab(sub); };
  const loc = gps.status === "live" ? state?.location : null;
  return (
    <GpsContext.Provider value={gps}>
      <div data-testid="driver-home">
        <MapView pickup={active?.pickup} dropoff={active?.dropoff} route={active?.route} driver={loc} />
        <TopBar />
        <section data-testid="driver-sheet"
          className={`sheet fade-up fixed inset-x-0 bottom-[60px] z-30 overflow-y-auto rounded-t-3xl p-5 pb-8 md:bottom-[76px] md:left-6 md:right-auto md:top-24 md:w-[440px] md:rounded-3xl md:p-6 ${tab === "home" ? "top-[30vh]" : "top-20"}`}>
          {tab === "home" && <Home active={active} go={go} onOpen={setOpen} />}
          {tab === "now" && <NowRides />}
          {tab === "scheduled" && <ScheduledRides tab={setsTab} setTab={setSetsTab} onOpen={setOpen} />}
          {tab === "calendar" && <DriverCalendar onOpen={setOpen} />}
          {tab === "history" && <div className="space-y-4"><h1 className="font-display text-2xl font-bold tracking-tight">Ride History</h1><RideHistory perspective="driver" /></div>}
          {tab === "messages" && <DriverMessages onOpen={setOpen} />}
          {tab === "profile" && <DriverProfile />}
        </section>
        <BottomNav value={tab} onChange={setTab} items={ITEMS} testid="driver-bottom-nav" />
        <RideDialog ride={open} onClose={() => setOpen(null)} />
      </div>
    </GpsContext.Provider>
  );
}
